/**
 * Inventory, seen from the books (T015, FR-129, research.md R9).
 *
 * THE BOOKS DO NOT IMPORT INVENTORY'S CODE. No module in this system imports
 * another; they talk over HTTP through the same public API any client uses. So
 * either module can be switched off — or deleted — without breaking the other's
 * build, which is the whole point of them being modules.
 *
 * The price of that independence is the copy below of inventory's quantity
 * entry rules. `tests/unit/books-stock-entry.test.ts` asserts the two agree
 * case for case, so the copy cannot drift in silence: a quantity that means
 * 200 ml in one module must never come to mean 0.2 ml in the other.
 */

export const UNITS = ["g", "kg", "ml", "l", "piece"] as const;
export type Unit = (typeof UNITS)[number];

/** Units whose thousandth has its own everyday name — typed in that name. */
const THOUSANDTH_NAME: Partial<Record<Unit, string>> = { l: "ml", kg: "g" };
const SCALE = 1000;
const DECIMALS = 3;

export class StockEntryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "StockEntryError";
  }
}

export function isUnit(value: unknown): value is Unit {
  return typeof value === "string" && (UNITS as readonly string[]).includes(value);
}

/** "l" → "ml", "kg" → "g", "piece" → "pc", anything else unchanged. */
export function entryUnit(unit: Unit): string {
  return THOUSANDTH_NAME[unit] ?? (unit === "piece" ? "pc" : unit);
}

/**
 * A received quantity as typed, in the unit people actually say out loud:
 * whole millilitres or grams for a litre or kilogram ingredient, otherwise the
 * unit itself to three decimals. Never negative — a delivery that took stock
 * away is wastage, and belongs in inventory's own form.
 */
export function parseStockEntry(text: string, unit: Unit): number {
  const trimmed = text.trim();

  if (THOUSANDTH_NAME[unit]) {
    const whole = /^(\d+)$/.exec(trimmed);
    if (!whole) {
      throw new StockEntryError(`"${text}" should be a whole number of ${entryUnit(unit)}.`);
    }
    const value = Number(whole[1]);
    if (!Number.isSafeInteger(value)) throw new StockEntryError(`"${text}" is too large.`);
    return value;
  }

  const match = /^(\d+)?(?:\.(\d+))?$/.exec(trimmed);
  if (!match || (match[1] === undefined && match[2] === undefined)) {
    throw new StockEntryError(`"${text}" is not a quantity.`);
  }

  const [, whole = "0", fraction = ""] = match;
  if (fraction.length > DECIMALS) {
    throw new StockEntryError(`"${text}" is finer than a thousandth. Use a smaller unit.`);
  }

  const value = Number(whole) * SCALE + Number(fraction.padEnd(DECIMALS, "0"));
  if (!Number.isSafeInteger(value)) throw new StockEntryError(`"${text}" is too large.`);
  return value;
}

// ---------------------------------------------------------------------------
// The client
// ---------------------------------------------------------------------------

export interface StockIngredient {
  id: string;
  name: string;
  unit: Unit;
  currentStock: number;
  active?: boolean;
}

/**
 * Inventory is not available to this person right now — switched off, not
 * permitted, or offline. Never an error the purchase form shows: it simply
 * stops offering stock lines and records the spending alone.
 */
export class StockUnavailable extends Error {
  constructor(
    readonly why: "off" | "not-allowed" | "offline",
    message: string,
  ) {
    super(message);
    this.name = "StockUnavailable";
  }
}

export function stockApi(token: string) {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`/api/inventory${path}`, {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          ...(body === undefined ? {} : { "content-type": "application/json" }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new StockUnavailable("offline", "No connection to stock.");
    }

    if (!response.ok) {
      // A disabled module answers 404 — it does not exist, rather than refusing.
      if (response.status === 404) throw new StockUnavailable("off", "Stock is switched off.");
      if (response.status === 403) throw new StockUnavailable("not-allowed", "Not allowed to record stock.");

      let message = `That did not go through (${response.status}).`;
      try {
        const parsed = (await response.json()) as { error?: { message?: string } };
        message = parsed.error?.message ?? message;
      } catch {
        // Not JSON — keep the generic sentence.
      }
      throw new Error(message);
    }

    return (await response.json()) as T;
  }

  return {
    ingredients: () => request<{ ingredients: StockIngredient[] }>("GET", "/ingredients"),

    /**
     * One call per ingredient, all of them carrying the purchase's single
     * `opId`. Inventory keys a movement on (op_id, ingredient_id), so the lines
     * never collide with each other and a retry of the same purchase records
     * once — which is what makes a half-finished save repairable by simply
     * pressing Save again.
     */
    receiving: (body: { opId: string; ingredientId: string; quantity: number; unitCost: number | null }) =>
      request<{ duplicate: boolean; delta: number }>("POST", "/receiving", body),
  };
}

export type StockApi = ReturnType<typeof stockApi>;
